/**
 * Context pour la gestion de session
 *
 * ARCHITECTURE SIMPLIFIÉE (Janvier 2026):
 * Fournit l'état de session et les fonctions de gestion à toute l'application
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useSession } from '../hooks/useSession';
import SessionWarningModal from '../components/SessionWarningModal';

interface SessionContextType {
  isAuthenticated: boolean;
  user: any;
  showWarning: boolean;
  timeUntilExpiry: number;
  setSession: (
    accessToken: string,
    refreshToken: string,
    expiresIn: number,
    user: any
  ) => void;
  refreshAccessToken: () => Promise<boolean>;
  logout: (skipApiCall?: boolean) => Promise<void>;
  dismissWarning: () => void;
  sendHeartbeat: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const session = useSession();

  return (
    <SessionContext.Provider value={session}>
      {children}
      <SessionWarningModal
        isOpen={session.showWarning}
        timeUntilExpiry={session.timeUntilExpiry}
        onStayConnected={session.refreshAccessToken}
        onLogout={() => session.logout()}
        onDismiss={session.dismissWarning}
      />
    </SessionContext.Provider>
  );
}

export function useSessionContext() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSessionContext must be used within a SessionProvider');
  }
  return context;
}
